import React, { useContext, useMemo } from 'react';
import "./InfoWindow.css";
import { MDBIcon } from 'mdb-react-ui-kit';
import { MDBAccordion, MDBAccordionItem } from 'mdb-react-ui-kit';
import { AppContext } from '../App';
import { nanoid } from 'nanoid';
import { Insight } from 'konect-model';

interface InfoWindowProps {
  onClose: () => void;
}

export enum InfoWindowState {
  IDEA_BOX = 1,
  GLYPH_SKETCHING
}

export default function InfoWindow(props: InfoWindowProps) {

  const { showInfo } = useContext(AppContext);

  let content;
  switch (showInfo) {
    case InfoWindowState.IDEA_BOX:
      content = <InsightWindow />
      break;
    case InfoWindowState.GLYPH_SKETCHING:
      content = <GlyphWindow />
      break;
    default:
      return <></>
  }

  return <div key={"info-window"}
    className={"info-window"}>
    <div className='info-window-content'>
      <div className='info-window-header'>
        <span className='info-window-header-name'>{showInfo === InfoWindowState.IDEA_BOX ? "Insight Information" : "Glyph Information"}        <span onClick={props.onClose} className={"info-window-close"} role="tab">
          <MDBIcon far icon="times-circle" />
        </span></span>
      </div>
      <div className='sidenav-horizontal-divider mt-3'></div>
      <div className='info-window-body'>
        {content}
        <p />
      </div>
    </div>
  </div>
}

function GlyphWindow() {
  return <>
   While creating Glyphs one should try to follow the following <span style={{ fontStyle: "italic" }}>Design Guidelines</span>:
   <p />
    <MDBAccordion alwaysOpen style={{ maxHeight: "55vh", overflowY: "auto" }}>
      {/*@ts-ignore*/}
      <MDBAccordionItem collapseId='accordionCollapse1' headerTitle={<>Consistency
      </>}
      >
        <strong>
          Use the same visual attribute for the same kind of insight for similar important
          information elements. <p />
        </strong>
        All information elements (i.e. rows) of all idea boxes are sorted according to their insights
        and the chosen visual property for each information is marked (e.g. color or length in the
        efﬁciency ranking column). Then these markings are checked to ensure that the same kind of
        insight (e.g. detect anomaly) is reﬂected by the same visual property (e.g. color).
        Furthermore, the same manifestation of the visual attribute needs to be chosen for the
        same kind of information (e.g. if red is an indicator for a failure, red should be used
        for this kind of information throughout the entire HMI design).
        The consistency is necessary for ensuring that pre-attentive perception of critical
        information is even possible at a global level.
      </MDBAccordionItem>
      <MDBAccordionItem collapseId='accordionCollapse2' headerTitle='Simplicity in Shapes'>
        <strong>
          Choose simple shapes and visual forms, choose non-accidental
          visual forms with regard to orientation. <p />
        </strong>
        To ensure simplicity, the metric for estimating the “goodness” of visual shapes must
        be applied. One indicator for visual form simplicity is it’s orientation. If a form is
        perceived as “non-accidental” with regard to orientation (thus it looks the same regardless
        of the viewer’s perspective) it can be understood as a simple form. For calculating the
        form simplicity, all visual shapes on the entire glyph designs are rotated and mirrored.
        Then the number of different appearances of the same form represents an indicator for a
        shapes goodness. The simpler a form is, the easier it can be recognized. More complex
        shapes lead to higher visual clutter causing a decreased recognition performance due to
        “occlusion, greater difﬁculty at both segmenting a scene and performing visual search”.
        Since this metric ﬁgured out to be initially difﬁcult to comprehend, the following figure
        shows an instruction card with an illustrating example.
        <div className="flex" style={{ textAlign: 'center' }}>
          <img
            src='./images/InstructionCardShapes.png'
            width='100%'
            alt=''
            loading='lazy'
          />
        </div>
      </MDBAccordionItem>
      <MDBAccordionItem collapseId='accordionCollapse3' headerTitle='Simplicity in Colors'>
        <strong>
          Reduce colors for elements that do not carry any information
          besides from structuring the interface. <p />
        </strong>
        Beside simplicity in shapes also simplicity in colors should be achieved in the user
        interface design. Therefore, each element on the design is systematically looked at
        and it is checked against the table whether a color appearing on the design encodes
        information or if it rather represents visual sugar. If the latter is the case, the color
        should be taken out by being replaced by a neutral color (e.g. grey). This step is
        especially important to maintain the pop-out effects produced by pre-attentive
        visible visual forms on a global level.
      </MDBAccordionItem>
      { /**
              <MDBAccordionItem collapseId='accordionCollapse4' headerTitle='Shortcuts'>
                <strong>
                  Define appropriate keyboard-shortcuts for each task that is not a duplicate.<p />
                </strong>
                Lorem ipsum sum summ summ summ.
              </MDBAccordionItem> */}
    </MDBAccordion>
    <p />
    Additionally the <span style={{ fontStyle: "italic" }}>Laws of Gestalt</span> should be kept in mind:
    <img src="./images/gestalt.png" alt="Gestalt Principles Diagram" style={{ width: "100%", margin: "20px 0" }} />
    <MDBAccordion alwaysOpen>
    <MDBAccordionItem key={nanoid()} collapseId={'accordionCollapse3'} headerTitle={"Proximity"}>
        Proximity refers to a spatial closeness of two visual elements. The closer
        two visual elements are to each other, the more the viewer will assign these
        elements to a conjunct group. This facilitates ”the detection and search for associated data” [Meirelles, 2013]. For instance, the viewer perceives
        three rows of circles, as the circles are spatially closer together on a horizontal
        line compared to the vertical distance of the circles.
      </MDBAccordionItem>
      <MDBAccordionItem key={nanoid()} collapseId={'accordionCollapse8'} headerTitle={"Similarity"}>
        Similarity describes the resemblance of the appearance of two visual elements. Owing to similarity, the viewer will group these elements into a perceptual
        unit. This is, for instance, essential for categorical association: ”
        For example, the
        use of color coding for categories can enhance search and comparison between
        them” [Meirelles, 2013]. In the example the viewer perceives again rows of circles as the circles appearance is the same in one horizontal row compared to the
        appearance of circles seen in columns.
      </MDBAccordionItem>
      <MDBAccordionItem key={nanoid()} collapseId={'accordionCollapse7'} headerTitle={"Enclosure"}>
        Enclosure is strongly coupled to the visual attribute of containment. The enclosure in the example is shown via one background color (first row) or via a
        surrounding line (fourth row). This leads to the aspect that the viewer assumes
        that these elements belong to one group and form a unit.

      </MDBAccordionItem>
      <MDBAccordionItem key={nanoid()} collapseId={'accordionCollapse1'} headerTitle={"Symmetry"}>
        Symmetry is used to visually join elements: Elements that build a
        symmetric form are grouped together. Furthermore, humans strive symmetry
        and prefer symmetric forms and perceptual constancy [Dickinson & Pizlo, 2013].
      </MDBAccordionItem>
      <MDBAccordionItem key={nanoid()} collapseId={'accordionCollapse6'} headerTitle={"Closure"}>
        ”The closure principle of perception describes our tendency to see bounded
        visual elements as wholes and to unite contours” [Meirelles, 2013].
        This is visualized exemplarily: the viewer will likely perceive a circle and a square
        instead of the single lines actually shown.
      </MDBAccordionItem>
      <MDBAccordionItem key={nanoid()} collapseId={'accordionCollapse5'} headerTitle={"Continuity"}>
        The law of continuity is closely coupled to the law of closure and describes
        the human tendency to close contours that represent a continuous form. Compared to the law of closure, the viewer will mentally continue a line and likely perceive this as a single visual element.
      </MDBAccordionItem>
      <MDBAccordionItem key={nanoid()} collapseId={'accordionCollapse4'} headerTitle={"Connection"}>
        The connection law states that the viewer will likely group visual elements
        together that are connected. In the example, the viewer perceives rows of circles
        instead of columns as the circles are connected horizontally. The law of connection
        is said to be quite a strong visual hint for viewers for grouping visual elements:
        ”The lines that connect various components [...] are a notation that is easy to read
        because the visual cortex of the brain contains mechanisms specifically designed
        to seek continuous contours. Other graphical notations for showing connectivity
        are far less effective” [Ware, 2004].
      </MDBAccordionItem>
      <MDBAccordionItem key={nanoid()} collapseId={'accordionCollapse2'} headerTitle={"Figure & Ground"}>
        ”The segregation between figures and ground principles describes
        the tendency to organize visual elements into units and construct relationships. In
        this process, some elements are selected as figures and the remaining as ground”
        [Meirelles, 2013]. In the example the viewer can either see a vase (deciding to perceive the black colored area as figure) or two faces looking at each other (deciding
        to perceive the white area as figure and the black part as ground).

      </MDBAccordionItem>
    </MDBAccordion>
    <p />
    <p />
    <div style={{fontWeight: "bold"}}>Handling view states:</div>
    States can be created to portray arbitrary system states (e.g. "critical speed" or "mental overload of an operator"). 
    By creating a state and then selecting it, it is possible to modify the drawn elements to visualize how they should look like in certain situations. 
    Example: You want display how the critical speed for a monitoring system of a vehicle looks like:
    At first you draw how the vehicle speed is generally displayed (by editing the default state) and then you could add a new state called "critical speed". 
    By selecting the "critical speed" state, you can now modify all UI elements that should represent the critical speed.
  </>
}

function InsightWindow() {
  const { project } = useContext(AppContext);
  const items = useMemo(() => {
    return project && project.insights.map((insight: Insight) => (
      <MDBAccordionItem key={nanoid()} collapseId={'accordionCollapse' + insight.id} headerTitle={insight.name}>
        <strong>
          Recommended attributes: <br />
          {insight.attributes.map((el: string, index: number) => el + "(" + index.toString() + "), ")}<br />
        </strong>
        {insight.description}
      </MDBAccordionItem>));
  }, [project])

  return <>
    <p />
    An insight describes the way an information is supposed to be understood by an individual.
    <p />
    <MDBAccordion alwaysOpen>
      {items}
    </MDBAccordion>
    <p />
  </>
}